import React, { useState, useEffect } from 'react';
import { 
  Mail, Send, Eye, RefreshCw, ToggleLeft, ToggleRight, Loader2, 
  CheckCircle, Settings, Clock, X, AlertCircle,
  UserCheck, ShoppingBag, Store, Building2, Shield, TestTube,
  Inbox
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

import { toast } from 'sonner';
import { formatLastSent } from '../../utils/dateUtils';

// Category configuration with V5 colors and glow effects
const categoryConfig = {
  authentication: { 
    label: 'Authentication', 
    icon: UserCheck, 
    color: '#06b6d4', // auth/cyan
    shadow: 'rgba(6, 182, 212, 0.5)',
    glow: 'rgba(6, 182, 212, 0.3)',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200'
  },
  consumer: { 
    label: 'Consumer', 
    icon: ShoppingBag, 
    color: '#f97316', // consumer/orange
    shadow: 'rgba(249, 115, 22, 0.5)',
    glow: 'rgba(249, 115, 22, 0.3)',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200'
  },
  business: { 
    label: 'Business', 
    icon: Store, 
    color: '#8b5cf6', // business/purple
    shadow: 'rgba(139, 92, 246, 0.5)',
    glow: 'rgba(139, 92, 246, 0.3)',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  },
  venue: { 
    label: 'Venue', 
    icon: Building2, 
    color: '#14b8a6', // venue/teal
    shadow: 'rgba(20, 184, 166, 0.5)',
    glow: 'rgba(20, 184, 166, 0.3)',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200'
  },
  admin: { 
    label: 'Admin', 
    icon: Shield, 
    color: '#6366f1', // admin/indigo
    shadow: 'rgba(99, 102, 241, 0.5)',
    glow: 'rgba(99, 102, 241, 0.3)',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200'
  }
};

const CLOSED_SEND_TEST = { open: false, template: null, loading: false };

const EmailTemplates = () => {
  const { user } = useAuth();
  // The admin the API authenticates (adminApi prefers adminToken); fall back to the auth-context user.
  const [adminEmail, setAdminEmail] = useState(null);
  const recipientEmail = adminEmail || user?.email || null;
  const [templates, setTemplates] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [toggling, setToggling] = useState(null);
  const [seeding, setSeeding] = useState(false);
  
  // Preview Modal State
  const [previewModal, setPreviewModal] = useState({ open: false, template: null, loading: false, content: null });
  
  // Send Test Modal State
  // Test sends always go to the calling admin's own address (the backend refuses any other).
  const [sendTestModal, setSendTestModal] = useState(CLOSED_SEND_TEST);

  useEffect(() => {
    fetchTemplates();
    fetchAdminEmail();
  }, []);

  const fetchAdminEmail = async () => {
    try {
      const response = await adminApi.get(`/admin/me`);
      if (response?.data?.email) setAdminEmail(response.data.email);
    } catch (err) {
      // Non-fatal: the auth-context email is shown instead.
      console.error('Failed to load admin profile:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get(`/admin/email-templates`);
      setTemplates(response.data.templates);
      setGrouped(response.data.grouped);
    } catch (err) {
      setError('Failed to load email templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (templateId, currentStatus) => {
    const tmpl = templates.find(t => t.id === templateId);
    if (tmpl?.always_on) return; // authentication emails cannot be disabled (server returns 400)
    try {
      setToggling(templateId);
      await adminApi.put(
        `/admin/email-templates/${templateId}?is_enabled=${!currentStatus}`,
        {}
      );
      
      // Update local state
      setTemplates(prev => prev.map(t => 
        t.id === templateId ? { ...t, is_enabled: !currentStatus } : t
      ));
      setGrouped(prev => {
        const newGrouped = { ...prev };
        Object.keys(newGrouped).forEach(cat => {
          newGrouped[cat] = newGrouped[cat].map(t =>
            t.id === templateId ? { ...t, is_enabled: !currentStatus } : t
          );
        });
        return newGrouped;
      });
      
      toast.success(`Template ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
    } catch (err) {
      console.error('Failed to toggle template:', err);
      toast.error(err?.response?.data?.detail || 'Failed to toggle template');
    } finally {
      setToggling(null);
    }
  };

  const handlePreview = async (template) => {
    setPreviewModal({ open: true, template, loading: true, content: null });
    
    try {
      const response = await adminApi.get(
        `/admin/email-templates/${template.id}/preview`
      );
      setPreviewModal(prev => ({ ...prev, loading: false, content: response.data }));
    } catch (err) {
      console.error('Failed to load preview:', err);
      setPreviewModal(prev => ({ 
        ...prev, 
        loading: false, 
        content: { 
          html_content: '<div style="padding: 40px; text-align: center;"><p style="color: #DC2626;">Failed to load preview</p></div>',
          subject: 'Error'
        }
      }));
    }
  };

  const handleSendTest = async () => {
    if (!sendTestModal.template) return;
    
    setSendTestModal(prev => ({ ...prev, loading: true }));
    
    try {
      // No recipient_email: the backend sends to the calling admin only.
      const response = await adminApi.post(
        `/admin/email-templates/${sendTestModal.template.id}/send-test`,
        {}
      );
      const data = response?.data || {};

      if (data.transport === 'log' || data.sent === false) {
        toast.info('Log transport: nothing was sent');
      } else {
        toast.success(`Test email sent to ${data.recipient || recipientEmail}`);
      }
      setSendTestModal(CLOSED_SEND_TEST);
    } catch (err) {
      console.error('Failed to send test email:', err);
      if (err.response?.status === 409) {
        toast.error('This template is behind a feature flag that is off');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to send test email');
      }
      setSendTestModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleSeedTemplates = async () => {
    try {
      setSeeding(true);
      await adminApi.post(
        `/admin/email-templates/seed`,
        {}
      );
      await fetchTemplates();
      toast.success('Templates seeded successfully');
    } catch (err) {
      console.error('Failed to seed templates:', err);
      toast.error('Failed to seed templates');
    } finally {
      setSeeding(false);
    }
  };

  const filteredGrouped = filter === 'all' 
    ? grouped 
    : { [filter]: grouped[filter] || [] };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        {/* V5 Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-consumer text-sm mb-1">// settings.emails</p>
            <h1 className="font-display text-3xl text-navy">Email Templates</h1>
            <p className="text-navy/50 mt-1">Manage automated email notifications ({templates.length} templates)</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchTemplates}
              className="px-4 py-2.5 border border-navy/20 rounded-xl text-navy/70 hover:bg-white hover:border-navy/30 transition-all flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handleSeedTemplates}
              disabled={seeding}
              className="px-5 py-2.5 bg-gradient-to-r from-consumer to-amber-400 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-consumer/20 transition-all flex items-center gap-2 disabled:opacity-50"
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
              {seeding ? 'Seeding...' : 'Seed Templates'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* V5 Category Filter Pills with Color-Coded Active States */}
        <div className="flex flex-wrap gap-3 mb-8">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-xl font-medium transition-all ${
              filter === 'all'
                ? 'bg-white text-navy shadow-sm'
                : 'text-navy/60 hover:bg-white/50'
            }`}
          >
            All ({templates.length})
          </button>
          {Object.entries(categoryConfig).map(([key, config]) => {
            const count = grouped[key]?.length || 0;
            const isActive = filter === key;
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-4 py-2 rounded-xl font-medium transition-all flex items-center gap-2 ${
                  isActive
                    ? 'text-white'
                    : 'text-navy/60 hover:bg-white/50'
                }`}
                style={isActive ? {
                  backgroundColor: config.color,
                  boxShadow: `0 4px 15px ${config.shadow}, 0 0 20px ${config.glow}`
                } : {}}
              >
                <config.icon className="w-4 h-4" />
                {config.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Template Cards by Category */}
        {Object.entries(filteredGrouped).map(([category, categoryTemplates]) => {
          if (!categoryTemplates || categoryTemplates.length === 0) return null;
          const config = categoryConfig[category];
          // A category with no entry above has no filter pill either, so it would
          // render as an unfilterable section (e.g. `rewards` while REWARDS is off).
          // Skip it: add a categoryConfig entry to surface a new category.
          if (!config) return null;
          const Icon = config.icon || Mail;
          
          return (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              {/* V5 Category Header */}
              <div className="flex items-center gap-3 mb-4 p-4 bg-white rounded-2xl shadow-sm border border-navy/5">
                <div className="w-10 h-10 bg-consumer/10 rounded-xl flex items-center justify-center">
                  <Icon className="w-5 h-5 text-consumer" />
                </div>
                <h2 className="font-display text-xl text-navy">
                  {config.label}
                </h2>
                <span className="ml-auto text-sm text-navy/50">
                  {categoryTemplates.length} template{categoryTemplates.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* V5 Template Cards */}
              <div className="space-y-3">
                {categoryTemplates.map((template) => (
                  <motion.div
                    key={template.id}
                    className={`bg-white rounded-2xl shadow-sm border border-navy/5 p-5 hover:shadow-md transition-all ${
                      !template.is_enabled ? 'opacity-60' : ''
                    }`}
                    whileHover={{ scale: 1.002 }}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-semibold text-navy">{template.name}</h3>
                          {template.is_enabled ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Active
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-navy/10 text-navy/50 text-xs font-medium rounded-full">
                              Disabled
                            </span>
                          )}
                          {template.audience && (
                            <span
                              className="px-2 py-0.5 bg-navy/5 text-navy/60 text-xs font-medium rounded-full"
                              data-testid={`audience-badge-${template.id}`}
                            >
                              Audience: {template.audience}
                            </span>
                          )}
                          {template.flag && (
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded-full font-mono ${
                                template.flag_enabled === false
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-navy/5 text-navy/60'
                              }`}
                              title={template.flag_enabled === false ? `Flag ${template.flag} is off` : `Flag ${template.flag} is on`}
                              data-testid={`flag-badge-${template.id}`}
                            >
                              Flag: {template.flag}{template.flag_enabled === false ? ' (off)' : ''}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-navy/60 mt-1">{template.description}</p>
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-navy/50">
                          <span className="flex items-center gap-1">
                            <Send className="w-3 h-3" />
                            Trigger: {template.trigger}
                          </span>
                          <span className="flex items-center gap-1">
                            <Inbox className="w-3 h-3" />
                            To: {template.recipients}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatLastSent(template.last_sent)}
                          </span>
                          {template.send_count > 0 && (
                            <span className="font-medium text-navy/70">
                              {template.send_count.toLocaleString()} sent
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* V5 Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePreview(template)}
                          className="px-3 py-1.5 border border-navy/20 rounded-lg text-sm text-navy/70 hover:bg-cream transition-colors flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          Preview
                        </button>
                        <button
                          onClick={() => setSendTestModal({ open: true, template, loading: false })}
                          className="px-3 py-1.5 border border-consumer/30 rounded-lg text-sm text-consumer hover:bg-consumer/10 transition-colors flex items-center gap-1"
                        >
                          <TestTube className="w-4 h-4" />
                          Test
                        </button>
                        <button
                          onClick={() => handleToggle(template.id, template.is_enabled)}
                          disabled={template.always_on || toggling === template.id}
                          aria-disabled={template.always_on ? 'true' : undefined}
                          data-testid={`toggle-${template.id}`}
                          className={`p-2 rounded-lg transition-all ${
                            template.always_on
                              ? 'bg-green-100 text-green-600 opacity-60 cursor-not-allowed'
                              : template.is_enabled
                              ? 'bg-green-100 text-green-600 hover:bg-green-200'
                              : 'bg-navy/10 text-navy/40 hover:bg-navy/20'
                          }`}
                          title={
                            template.always_on
                              ? 'Authentication emails are always on'
                              : template.is_enabled ? 'Click to disable' : 'Click to enable'
                          }
                          aria-label={
                            template.always_on
                              ? `${template.name}: authentication emails are always on`
                              : `${template.is_enabled ? 'Disable' : 'Enable'} ${template.name}`
                          }
                        >
                          {toggling === template.id ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : template.always_on || template.is_enabled ? (
                            <ToggleRight className="w-5 h-5" />
                          ) : (
                            <ToggleLeft className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          );
        })}

        {templates.length === 0 && !loading && (
          <div className="bg-white rounded-2xl shadow-sm border border-navy/5 py-20 text-center">
            <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-navy/20" />
            </div>
            <h3 className="font-display text-xl text-navy mb-2">No email templates configured</h3>
            <p className="text-navy/50">Click &quot;Seed Templates&quot; to initialize all templates</p>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {previewModal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setPreviewModal({ open: false, template: null, loading: false, content: null })}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* V5 Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-navy/5 bg-cream">
                <div>
                  <h3 className="font-display text-lg text-navy flex items-center gap-2">
                    <Eye className="w-5 h-5 text-consumer" />
                    Preview: {previewModal.template?.name}
                  </h3>
                  {previewModal.content?.subject && (
                    <p className="text-sm text-navy/50 mt-1">Subject: {previewModal.content.subject}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setPreviewModal({ open: false, template: null, loading: false, content: null });
                      setSendTestModal({ open: true, template: previewModal.template, loading: false });
                    }}
                    className="px-3 py-1.5 border border-consumer/30 rounded-lg text-sm text-consumer hover:bg-consumer/10 transition-colors flex items-center gap-1"
                  >
                    <TestTube className="w-4 h-4" />
                    Send Test
                  </button>
                  <button
                    onClick={() => setPreviewModal({ open: false, template: null, loading: false, content: null })}
                    aria-label="Close preview"
                    className="p-2 hover:bg-white rounded-lg transition-colors text-navy/50 hover:text-navy"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Modal Content */}
              <div className="flex-1 overflow-auto bg-cream p-4">
                {previewModal.loading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-consumer" />
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden max-w-2xl mx-auto">
                    <div 
                      className="email-preview"
                      dangerouslySetInnerHTML={{ __html: previewModal.content?.html_content || '' }}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Send Test Modal */}
      <AnimatePresence>
        {sendTestModal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSendTestModal(CLOSED_SEND_TEST)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="send-test-title"
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              {/* V5 Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-navy/5 bg-cream">
                <h3 id="send-test-title" className="font-display text-lg text-navy flex items-center gap-2">
                  <TestTube className="w-5 h-5 text-consumer" />
                  Send Test Email
                </h3>
                <button
                  onClick={() => setSendTestModal(CLOSED_SEND_TEST)}
                  aria-label="Close"
                  className="p-2 hover:bg-white rounded-lg transition-colors text-navy/50 hover:text-navy"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* V5 Modal Content */}
              <div className="p-6">
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-navy/50" />
                    <span className="font-medium text-navy">{sendTestModal.template?.name}</span>
                  </div>
                  <p className="text-sm text-navy/50">{sendTestModal.template?.description}</p>
                </div>
                
                <div className="mb-6 p-4 bg-cream border border-navy/10 rounded-xl" data-testid="send-test-recipient">
                  <p className="text-sm text-navy">
                    Sends to <span className="font-medium">{recipientEmail || 'your admin email address'}</span>
                  </p>
                  <p className="text-xs text-navy/50 mt-1">
                    Test emails only go to your own admin address.
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setSendTestModal(CLOSED_SEND_TEST)}
                    className="flex-1 px-4 py-3 border border-navy/20 rounded-xl text-navy/70 font-medium hover:bg-cream transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendTest}
                    disabled={sendTestModal.loading}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-consumer to-amber-400 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-consumer/20 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {sendTestModal.loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Test
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};

export default EmailTemplates;
